/**
 * transactionChecklistPresenter — FE-AUD-76 mapping into LaisChecklistPanel's
 * item shape ({ id, label, status, owner, deadline, evidenceLabel,
 * evidenceRedacted, blocker, actionLabel }). It also derives the operational
 * action list, installments and redacted history consumed by the wrapper.
 *
 * Two sources, in order of fidelity:
 *  1. A real closing transaction (`PropertyTransactionSerializer`, when the
 *     caller resolved one) with full checklist_items — evidence is exposed only
 *     as a redacted count descriptor ({ key, count }) to be translated by the
 *     caller, never as raw refs, and waivers surface as an `na` row.
 *  2. Offer-scoped documentation/KYC fallback when no transaction is linked —
 *     honest partial view built from `documentation_status` + timeline steps.
 *
 * The controller enforces the item state machine; this module only maps status
 * to the canonical checklist status and derives the allowed row action.
 */

// checklist item status -> LaisChecklistPanel status
const ITEM_STATUS_MAP = Object.freeze({
  done: 'done',
  waived: 'na',
  blocked: 'blocked',
  in_progress: 'pending',
  pending: 'pending',
});

// document/KYC status -> LaisChecklistPanel status
const KYC_STATUS_MAP = Object.freeze({
  approved: 'done',
  aprovado: 'done',
  verified: 'done',
  pending: 'pending',
  pendente: 'pending',
  em_analise: 'pending',
  in_review: 'pending',
  rejected: 'blocked',
  reprovado: 'blocked',
  failed: 'blocked',
  missing: 'pending',
});

// i18n contract: this presenter never returns ready-made pt-BR copy. Labels the
// backend does not provide are returned as stable keys (+ params) under
// PROPERTY_OFFERS.CHECKLIST and translated by the consuming component.
export const CHECKLIST_I18N_KEYS = Object.freeze({
  EVIDENCE_COUNT_ONE: 'PROPERTY_OFFERS.CHECKLIST.EVIDENCE_COUNT_ONE',
  EVIDENCE_COUNT_OTHER: 'PROPERTY_OFFERS.CHECKLIST.EVIDENCE_COUNT_OTHER',
  DOCUMENTATION_ITEM: 'PROPERTY_OFFERS.CHECKLIST.DOCUMENTATION_ITEM',
});

const normalize = value => String(value ?? '').toLowerCase();

export const mapItemStatus = status =>
  ITEM_STATUS_MAP[normalize(status)] || 'pending';

const evidenceRefsFor = item => {
  if (Array.isArray(item?.evidence?.refs)) return item.evidence.refs;
  if (Array.isArray(item?.evidence_refs)) return item.evidence_refs;
  return [];
};

const evidenceCountFor = item => {
  const count = item?.evidence?.count;
  if (Number.isFinite(count)) return count;
  return evidenceRefsFor(item).length;
};

// Returns an i18n descriptor ({ key, count }) instead of translated text so the
// evidence counter can be localized by the caller. `null` means "no evidence".
export const evidenceLabelFor = item => {
  const count = evidenceCountFor(item);
  if (count === 0) return null;
  return {
    key:
      count === 1
        ? CHECKLIST_I18N_KEYS.EVIDENCE_COUNT_ONE
        : CHECKLIST_I18N_KEYS.EVIDENCE_COUNT_OTHER,
    count,
  };
};

/**
 * Allowed row action for a checklist item, mirroring the ChecklistService
 * transitions. Waiver is intentionally NOT surfaced as a generic row action —
 * it requires an authorized role and its own reason modal.
 */
export const itemAction = item => {
  const status = normalize(item?.status);
  if (status === 'done' || status === 'waived') return 'reopen';
  if (status === 'blocked') {
    return item?.dependency_satisfied === true ? 'unblock' : '';
  }
  if (item?.requires_evidence && evidenceCountFor(item) === 0) {
    return 'add_evidence';
  }
  return 'complete';
};

export const availableItemActions = (item, { canWaive = false } = {}) => {
  const primary = itemAction(item);
  const status = normalize(item?.status);
  const actions = primary ? [primary] : [];
  if (!['done', 'waived'].includes(status)) {
    actions.push('add_evidence');
  }
  if (canWaive && !['done', 'waived'].includes(status)) actions.push('waive');
  return [...new Set(actions)];
};

const progressPercentFor = transaction => {
  const progress = transaction.progress;
  if (typeof progress === 'number') return progress;
  if (typeof progress?.percent === 'number') return progress.percent;
  return null;
};

export function presentInstallments(installments = []) {
  return installments.map(installment => ({
    id: installment.id,
    sequence: installment.sequence,
    label: `#${installment.sequence}`,
    amount: installment.amount,
    currency: installment.currency || 'BRL',
    paidAmount: installment.paid_amount,
    balanceAmount: installment.balance_amount,
    dueDate: installment.due_date || null,
    status: installment.status || 'pending',
    settled: installment.settled === true,
  }));
}

export function presentChecklistHistory(events = []) {
  return events
    .slice(-12)
    .reverse()
    .map(event => ({
      id: event.id,
      itemSlug: event.item_slug || '',
      eventType: event.event_type || '',
      fromStatus: event.from_status || '',
      toStatus: event.to_status || '',
      actor: event.actor?.name || '',
      reason: event.reason || '',
      reasonRedacted: event.reason_redacted === true,
      hasEvidence: event.has_evidence === true,
      occurredAt: event.occurred_at || null,
    }));
}

export const presentTransactionChecklist = (transaction = {}) => {
  const rawItems = transaction.checklist_items || [];
  const canWaive = transaction.permissions?.can_waive_item === true;
  const items = rawItems
    .slice()
    .sort((a, b) => (a.position || 0) - (b.position || 0))
    .map(item => {
      const evidenceCount = evidenceCountFor(item);
      return {
        id: item.id,
        sourceId: item.id,
        slug: item.slug || '',
        category: item.category || '',
        label: item.title || item.slug || '',
        // Backend-provided title; there is no fallback copy to translate here.
        labelKey: '',
        rawStatus: item.status,
        status: mapItemStatus(item.status),
        owner: item.owner?.name || '',
        deadline: item.deadline_at || null,
        completedAt: item.completed_at || null,
        reopenedCount: item.reopened_count || 0,
        waivedAt: item.waived_at || item.waiver?.waived_at || null,
        waiver: item.waiver || null,
        // Evidence refs are already redacted by the backend and are reduced to
        // a count/lock in this presenter; raw URLs or provider tokens are never
        // copied into the view model.
        evidenceCount,
        evidenceLabel: evidenceLabelFor(item),
        evidenceRedacted: item.evidence?.redacted === true || evidenceCount > 0,
        blocker:
          normalize(item.status) === 'blocked' &&
          item.dependency_satisfied === false
            ? 'dependency'
            : '',
        mandatory: item.mandatory === true,
        requiresEvidence: item.requires_evidence === true,
        actionKey: itemAction(item),
        actions: availableItemActions(item, { canWaive }),
      };
    });
  return {
    source: 'transaction',
    items,
    progress: progressPercentFor(transaction),
    counts: transaction.item_counts || {},
    summary: transaction.checklist_summary || {},
    installments: presentInstallments(transaction.installments || []),
    installmentSummary: transaction.installment_summary || {},
    history: presentChecklistHistory(transaction.checklist_history || []),
    permissions: transaction.permissions || {},
    mandatoryPending: transaction.mandatory_items_pending === true,
    reference: transaction.reference || '',
  };
};

/**
 * Offer-scoped fallback: builds a minimal checklist from the offer's
 * documentation status and negotiation steps when no closing transaction is
 * available for the offer. Clearly partial — surfaces should label it as the
 * documentation view, not the full transaction checklist.
 */
export const presentDocumentationChecklist = (offer = {}) => {
  const items = [];
  const doc = offer.documentation_status || offer.kyc_status;
  if (doc) {
    items.push({
      id: `doc-${doc.verification_id || 'kyc'}`,
      label: doc.label || '',
      labelKey: doc.label ? '' : CHECKLIST_I18N_KEYS.DOCUMENTATION_ITEM,
      status: KYC_STATUS_MAP[normalize(doc.status)] || 'pending',
      owner: '',
      deadline: null,
      evidenceLabel: null,
      evidenceRedacted: Boolean(doc.verification_id),
      blocker: normalize(doc.status) === 'reprovado' ? 'rejected' : '',
      actionKey: '',
    });
  }
  const STEP_STATUS = { concluido: 'done', cancelado: 'blocked' };
  (offer.timeline || offer.timeline_payload?.steps || []).forEach(step => {
    const state = normalize(step.state);
    if (state === 'ignorado') return;
    items.push({
      id: `step-${step.key}`,
      label: step.label || step.key,
      labelKey: '',
      status: STEP_STATUS[state] || 'pending',
      owner: '',
      deadline: null,
      evidenceLabel: null,
      evidenceRedacted: false,
      blocker: '',
      actionKey: '',
    });
  });
  return { source: 'documentation', items, partial: true };
};

export const presentChecklist = ({ transaction, offer } = {}) => {
  if (transaction && (transaction.checklist_items || transaction.id)) {
    return presentTransactionChecklist(transaction);
  }
  return presentDocumentationChecklist(offer || {});
};

export default {
  CHECKLIST_I18N_KEYS,
  evidenceLabelFor,
  mapItemStatus,
  itemAction,
  availableItemActions,
  presentTransactionChecklist,
  presentInstallments,
  presentChecklistHistory,
  presentDocumentationChecklist,
  presentChecklist,
};
